/**
 * Sample data, mixed in with the real data on every screen — one sample
 * client per scenario the app can be in, so every state, pill, filter and
 * drawer has something to show while real data is still thin.
 *
 * ── Turning it off ──────────────────────────────────────────────────────────
 * Off in the real app — only the demo build turns it on. Every merge point in
 * `api.js` / `ledgerApi.js` checks this flag.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Safety, since real and sample rows share tables:
 *   - every sample client code starts `SAMPLE-` and every id `sample-`, and
 *     rows show a "Sample" tag
 *   - nothing sample ever reaches the backend: `api.js` answers sample reads
 *     from here and refuses sample writes (send, pull, reconcile, mark done…)
 *     with `SAMPLE_REFUSAL`, and strips sample codes out of bulk actions
 */

// Off in the real app; on only in the one-file demo (`npm run build:demo`).
export const SHOW_SAMPLES = import.meta.env.VITE_DEMO === '1'

export const SAMPLE_REFUSAL = 'This is sample data — nothing was sent, saved or run.'

export const isSampleCode = (code) => String(code ?? '').startsWith('SAMPLE-')
export const isSampleId = (id) => String(id ?? '').startsWith('sample-')
/** Any path that names a sample record, by id or client code. */
export const isSamplePath = (path) => /\/(sample-|SAMPLE-)/.test(String(path))

// ── Dates, relative to today so the samples never go stale ──────────────────

const DAY = 86_400_000
const pad = (n) => String(n).padStart(2, '0')
const isoDay = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
/** `YYYY-MM-DD`, `offset` days from today. */
const day = (offset) => isoDay(new Date(Date.now() + offset * DAY))
/** An ISO instant `offset` days from now, at `hour`:`min` IST-ish. */
const at = (offset, hour = 11, min = 0) => {
  const d = new Date(Date.now() + offset * DAY)
  d.setHours(hour, min, 0, 0)
  return d.toISOString()
}
const fyStart = () => {
  const now = new Date()
  const y = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1
  return `${y}-04-01`
}
const monthEnd = (offsetMonths) => {
  const now = new Date()
  return isoDay(new Date(now.getFullYear(), now.getMonth() + offsetMonths + 1, 0))
}

// ── The scenarios ────────────────────────────────────────────────────────────

/**
 * One entry per sample client. `req` overrides a default "asked, no reply"
 * request; `req: null` means no request exists yet (never asked).
 */
const SCENARIOS = [
  { n: '01', name: 'Aarav Jewels', bal: 1245000_00, side: 'Dr', wa: '+919800000001', email: 'accounts@aarav.example', req: null },
  { n: '02', name: 'Bhavya Gold House', bal: 382500_50, side: 'Dr', wa: null, email: null, req: null },
  {
    n: '03', name: 'Chandan Ornaments', bal: 2210400_00, side: 'Dr', wa: '+919800000003', email: 'chandan@example.com',
    req: { chase: { WHATSAPP: 4 }, state: 'AWAITING_REPLY', chaseDueOn: day(0), requestedAt: at(-9) },
  },
  {
    n: '04', name: 'Devika Diamonds', bal: 915000_00, side: 'Dr', wa: null, email: 'finance@devika.example',
    req: { chase: { EMAIL: 3 }, state: 'AWAITING_REPLY', chaseDueOn: day(-3), requestedAt: at(-16) },
  },
  {
    n: '05', name: 'Ekta Bullion', bal: 4710000_00, side: 'Dr', wa: '+919800000005', email: null,
    req: { chase: { WHATSAPP: 3 }, state: 'PROMISED', promisedFor: day(3), chaseDueOn: null, requestedAt: at(-8) },
  },
  {
    n: '06', name: 'Falguni Creations', bal: 128900_00, side: 'Dr', wa: '+919800000006', email: 'falguni@example.com',
    req: { chase: { WHATSAPP: 5, EMAIL: 1 }, state: 'PROMISE_LATE', promisedFor: day(-2), chaseDueOn: day(-1), requestedAt: at(-38) },
  },
  {
    n: '07', name: 'Gauri Gems', bal: 675320_00, side: 'Dr', wa: '+919800000007', email: null,
    req: { chase: { WHATSAPP: 2 }, state: 'AWAITING_REPLY', disputed: true, chaseDueOn: day(1), requestedAt: at(-6), unread: 1 },
  },
  {
    n: '08', name: 'Harsh Jewellers', bal: 305000_00, side: 'Dr', wa: '+919800000008', email: 'harsh@example.com',
    req: { chase: { WHATSAPP: 3 }, state: 'AWAITING_REPLY', reply: 'UNCLEAR', chaseDueOn: day(1), requestedAt: at(-7), unread: 2 },
  },
  {
    n: '09', name: 'Ishaan Traders', bal: 88000_00, side: 'Dr', wa: '+919800000009', email: null,
    req: { chase: { WHATSAPP: 2 }, state: 'THEY_REPLIED', chaseDueOn: day(0), requestedAt: at(-5), unread: 1 },
  },
  {
    n: '10', name: 'Jaya Silver Works', bal: 1540000_00, side: 'Dr', wa: '+919800000010', email: 'jaya@example.com',
    req: { chase: { WHATSAPP: 3 }, state: 'DOC_WRONG_PERIOD', chaseDueOn: day(1), requestedAt: at(-11) },
  },
  {
    n: '11', name: 'Kiran Chains', bal: 2290000_00, side: 'Dr', wa: '+919800000011', email: null,
    req: {
      chase: { WHATSAPP: 2 }, state: 'RECEIVED', status: 'RECEIVED', chaseDueOn: null, requestedAt: at(-10),
      their: { receivedAt: at(-1, 15), source: 'WA', entryCount: 64, confirmedAt: null },
    },
  },
  {
    n: '12', name: 'Lakshmi Ornaments', bal: 3350000_00, side: 'Dr', wa: '+919800000012', email: 'lakshmi@example.com',
    req: {
      chase: { WHATSAPP: 1, EMAIL: 1 }, state: 'RECEIVED', status: 'READY', chaseDueOn: null, requestedAt: at(-14),
      their: { receivedAt: at(-6, 12), source: 'EMAIL', entryCount: 118, confirmedAt: at(-6, 13) },
      ours: { entryCount: 121 },
      run: { status: 'success', rectificationSide: 'none' },
    },
  },
  {
    n: '13', name: 'Meera Gold Palace', bal: 5125000_00, side: 'Dr', wa: '+919800000013', email: null,
    req: {
      chase: { WHATSAPP: 3 }, state: 'RECEIVED', status: 'READY', chaseDueOn: null, requestedAt: at(-18),
      their: { receivedAt: at(-9, 10), source: 'WA', entryCount: 203, confirmedAt: at(-9, 11) },
      ours: { entryCount: 209 },
      run: { status: 'success', rectificationSide: 'client' },
      postRecoAsks: 7,
    },
  },
  {
    n: '14', name: 'Nandini Jewels', bal: 410000_00, side: 'Dr', wa: '+919800000014', email: null,
    req: {
      chase: { WHATSAPP: 3 }, state: 'AWAITING_REPLY', chaseDueOn: day(0), requestedAt: at(-12),
      sendFailed: true, sendFailedReason: 'WhatsApp template not approved', sendFailedSince: at(-4),
    },
  },
  {
    n: '15', name: 'Om Sai Bullion', bal: 760000_00, side: 'Dr', wa: '+919800000015', email: null,
    req: { chase: { WHATSAPP: 2 }, state: 'BAD_CONTACT', chaseDueOn: null, requestedAt: at(-8), badContactChannel: 'WHATSAPP' },
  },
  {
    n: '16', name: 'Pooja Pearls', bal: 99000_00, side: 'Dr', wa: '+919800000016', email: 'pooja@example.com',
    req: { chase: { WHATSAPP: 4, EMAIL: 2 }, state: 'REFUSED', chaseDueOn: null, requestedAt: at(-45) },
  },
  {
    n: '17', name: 'Qadir & Sons', bal: 1890000_00, side: 'Dr', wa: '+919800000017', email: null,
    req: {
      chase: { WHATSAPP: 2 }, state: 'RECEIVED', status: 'RECEIVED', chaseDueOn: null, requestedAt: at(-9),
      their: { receivedAt: at(-2, 16), source: 'UPLOAD', entryCount: 87, confirmedAt: at(-2, 16) },
      ours: { entryCount: 0, lastError: 'Gati SingleLedgerTransaction unreachable: timeout' },
    },
  },
  { n: '18', name: 'Riya Diamonds', bal: 640000_00, side: 'Cr', wa: '+919800000018', email: 'riya@example.com', autoChase: true,
    req: { chase: { WHATSAPP: 1 }, state: 'AWAITING_REPLY', chaseDueOn: day(2), requestedAt: at(-2) },
  },
  {
    n: '19', name: 'Shreeji Gold', bal: 2750000_00, side: 'Dr', wa: '+919800000019', email: null,
    req: { chase: { WHATSAPP: 2 }, state: 'REDIRECTED', chaseDueOn: day(0), requestedAt: at(-6) },
  },
  {
    n: '20', name: 'Tanvi Creations', bal: 1175000_00, side: 'Dr', wa: '+919800000020', email: 'tanvi@example.com',
    req: {
      chase: { WHATSAPP: 2 }, state: 'RECEIVED', status: 'READY', chaseDueOn: null, requestedAt: at(-13),
      their: { receivedAt: at(-3, 11), source: 'WA', entryCount: 96, confirmedAt: at(-3, 12) },
      ours: { entryCount: 98, reconciles: false },
    },
  },
  {
    n: '21', name: 'Ujjwal Gold Co', bal: 455000_00, side: 'Dr', wa: '+919800000021', email: null,
    req: { chase: { WHATSAPP: 2 }, state: 'AWAITING_REPLY', reply: 'QUESTION', chaseDueOn: day(1), requestedAt: at(-4), unread: 1 },
  },
  {
    n: '22', name: 'Vihaan Jewels', bal: 2025000_00, side: 'Dr', wa: '+919800000022', email: 'vihaan@example.com',
    req: { chase: { WHATSAPP: 3, EMAIL: 1 }, state: 'AWAITING_REPLY', reply: 'CLAIMS_ALREADY_SENT', chaseDueOn: day(1), requestedAt: at(-10) },
  },
  {
    n: '23', name: 'Yash Ornaments', bal: 330000_00, side: 'Dr', wa: '+919800000023', email: null,
    req: { chase: { WHATSAPP: 2 }, state: 'AWAITING_REPLY', reply: 'WRONG_CONTACT', chaseDueOn: day(2), requestedAt: at(-7) },
  },
  {
    n: '24', name: 'Zoya Diamonds', bal: 1420000_00, side: 'Dr', wa: '+919800000024', email: null,
    req: { chase: { WHATSAPP: 3 }, state: 'DOC_UNREADABLE', chaseDueOn: day(0), requestedAt: at(-9) },
  },
  {
    n: '25', name: 'Anmol Silver', bal: 265000_00, side: 'Dr', wa: '+919800000025', email: 'anmol@example.com',
    req: { chase: { WHATSAPP: 2 }, state: 'DOC_NOT_STATEMENT', chaseDueOn: day(0), requestedAt: at(-5) },
  },
]

const code = (s) => `SAMPLE-${s.n}`
const reqId = (s) => `sample-req-${s.n}`

// ── Ledger Collector ─────────────────────────────────────────────────────────

/** Rows shaped like `normaliseLedgerRow`'s output (the Track balance list). */
export function sampleLedgerRows() {
  return SCENARIOS.map((s) => ({
    clientCode: code(s),
    clientName: s.name,
    category: s.side === 'Cr' ? 'Sundry Creditors' : 'Sundry Debtors',
    locationName: 'Sample',
    side: s.side,
    balancePaise: s.bal,
    pinned: false,
    sample: true,
  }))
}

/** What `listClientScreenData` adds for the samples. */
export function sampleScreenData() {
  const contacts = {}
  for (const s of SCENARIOS) contacts[code(s)] = { whatsAppNo: s.wa, email: s.email }
  return { contacts, autoChaseCodes: SCENARIOS.filter((s) => s.autoChase).map(code) }
}

function sampleRequest(s, period) {
  const r = s.req
  const counts = { WHATSAPP: 0, EMAIL: 0, CALL: 0, OTHER: 0, ...r.chase }
  const chaseCount = counts.WHATSAPP + counts.EMAIL + counts.CALL + counts.OTHER
  const their = r.their ?? {}
  const ours = r.ours ?? null
  const pulled = Boolean(ours)
  return {
    _id: reqId(s),
    sample: true,
    clientCode: code(s),
    clientName: s.name,
    periodFrom: fyStart(),
    periodTo: period?.to ?? monthEnd(-1),
    periodLabel: period?.label ?? 'Sample',
    status: r.status ?? 'REQUESTED',
    chaseCount,
    chaseCounts: counts,
    failedCounts: r.sendFailed ? { WHATSAPP: 1 } : {},
    postReco: r.postRecoAsks ? { asks: r.postRecoAsks, needsPerson: r.postRecoAsks >= 7, lastRestartAt: null } : null,
    conversation: {
      state: r.state,
      lastOutboundAt: at(-1),
      lastInboundAt: r.state === 'AWAITING_REPLY' && !r.disputed && !r.reply ? null : at(-1, 14),
      disputed: Boolean(r.disputed),
      promisedFor: r.promisedFor ?? null,
      unread: r.unread ?? 0,
      sendFailed: Boolean(r.sendFailed),
      sendFailedReason: r.sendFailedReason ?? null,
      sendFailedSince: r.sendFailedSince ?? null,
      badContactChannel: r.badContactChannel ?? null,
    },
    createdAt: r.requestedAt,
    requestedAt: r.requestedAt,
    lastChaseAt: at(-1),
    lastChaseChannel: counts.WHATSAPP ? 'WHATSAPP' : 'EMAIL',
    nextDueOn: their.receivedAt ? day(28) : r.requestedAt.slice(0, 10),
    chaseDueOn: r.chaseDueOn ?? null,
    ourSide: {
      pulledAt: pulled ? at(-1) : null,
      ledgerDocId: pulled && !ours.lastError ? 'sample-doc' : null,
      ledgerFrom: pulled ? fyStart() : null,
      ledgerTo: pulled ? day(0) : null,
      entryCount: ours?.entryCount ?? 0,
      reconciles: ours?.reconciles ?? pulled,
      lastError: ours?.lastError ?? null,
    },
    theirSide: {
      docId: their.receivedAt ? 'sample-doc' : null,
      receivedAt: their.receivedAt ?? null,
      source: their.source ?? null,
      entryCount: their.entryCount ?? 0,
      confirmedAt: their.confirmedAt ?? null,
    },
  }
}

/** Sample requests for the Collector's current period (only scenarios that have one). */
export function sampleRequests(period) {
  return SCENARIOS.filter((s) => s.req).map((s) => sampleRequest(s, period))
}

export function sampleSummary(requests) {
  const out = { NOT_REQUESTED: 0, REQUESTED: 0, RECEIVED: 0, READY: 0, total: requests.length }
  for (const r of requests) out[r.status] = (out[r.status] ?? 0) + 1
  return out
}

/** Latest run per sample request, for the Collector's reconciliation filter. */
export function sampleLatestRuns() {
  return SCENARIOS.filter((s) => s.req?.run).map((s) => ({
    ledgerRequestId: reqId(s),
    runId: `sample-run-${s.n}`,
    status: s.req.run.status,
    rectificationSide: s.req.run.rectificationSide,
    createdAt: at(-4),
  }))
}

const findScenario = (idOrCode) =>
  SCENARIOS.find((s) => reqId(s) === idOrCode || code(s) === idOrCode || idOrCode?.startsWith?.(`${reqId(s)}`))

// ── Ledger rows (LedgerView) ─────────────────────────────────────────────────

function sampleEntries(side, count) {
  const out = []
  const start = new Date(`${fyStart()}T00:00:00`)
  for (let i = 0; i < count; i++) {
    const d = isoDay(new Date(start.getTime() + Math.floor((i * 170) / Math.max(count, 1)) * DAY))
    const receipt = i % 4 === 3
    const amt = (((i * 7919) % 900) + 50) * 1000_00
    out.push({
      _id: `sample-e-${side}-${i}`,
      side,
      txnDate: d,
      voucherNo: receipt ? `KTK/26-27/${pad(1000 + i)}` : `JWS/26-27/${pad(100 + i)}`,
      referenceNo: null,
      narration: receipt ? 'Kotak Bank CC · receipt' : 'HQ Jewelry Sale · sample invoice',
      debitPaise: side === 'OURS' ? (receipt ? 0 : amt) : receipt ? amt : 0,
      creditPaise: side === 'OURS' ? (receipt ? amt : 0) : receipt ? 0 : amt,
      confidence: 'high',
    })
  }
  return out
}

export function sampleRequestDetail(id, period) {
  const s = findScenario(id)
  if (!s?.req) return null
  const request = sampleRequest(s, period)
  return {
    request,
    ours: sampleEntries('OURS', request.ourSide.entryCount),
    theirs: sampleEntries('CLIENT', request.theirSide.entryCount),
  }
}

// ── Conversation thread (ActivityDrawer) ────────────────────────────────────

const out = (id, offset, body, outcome = 'READ', extra = {}) => ({
  kind: 'chase',
  id,
  providerMsgId: null,
  at: at(offset, 11),
  channel: 'WHATSAPP',
  direction: 'OUT',
  outcome,
  note: null,
  actor: 'system',
  templateKey: 'REQUEST_WA_2',
  subject: null,
  body,
  promisedFor: null,
  media: [],
  deliveredAt: at(offset, 11, 1),
  readAt: outcome === 'READ' ? at(offset, 12) : null,
  failedAt: outcome === 'FAILED' ? at(offset, 11, 1) : null,
  failReason: outcome === 'FAILED' ? 'Template not approved' : null,
  ...extra,
})
const inbound = (id, offset, body, outcome, extra = {}) => ({
  kind: 'chase',
  id,
  providerMsgId: null,
  at: at(offset, 14),
  channel: 'WHATSAPP',
  direction: 'IN',
  outcome,
  note: null,
  actor: 'system',
  templateKey: null,
  subject: null,
  body,
  promisedFor: null,
  media: [],
  deliveredAt: null,
  readAt: null,
  failedAt: null,
  failReason: null,
  ...extra,
})

const REPLIES = {
  PROMISED: (d) => inbound('i1', -1, 'Kal tak bhej denge.', 'PROMISED', { promisedFor: d.req.promisedFor }),
  PROMISE_LATE: (d) => inbound('i1', -4, 'Monday tak bhej dunga.', 'PROMISED', { promisedFor: d.req.promisedFor }),
  DISPUTED: () => inbound('i1', -1, 'Aapke ledger mein ₹2,40,000 zyada dikh raha hai, invoice JWS/0452 hamara nahi hai.', 'DISPUTES_FIGURE'),
  UNCLEAR: () => inbound('i1', -1, 'Ye kaunse period ka chahiye? Humne pichle mahine bheja tha na?', 'UNCLEAR'),
  THEY_REPLIED: () => inbound('i1', -1, 'Ok, accountant se baat karke bhejta hoon.', 'REPLIED'),
  DOC_WRONG_PERIOD: () =>
    inbound('i1', -2, null, 'DOC_WRONG_PERIOD', {
      note: 'Statement covers Apr 2025 – Mar 2026, before the period we asked for',
      media: [{ type: 'DOCUMENT', fileName: 'ledger-fy25.pdf', mime: 'application/pdf', sizeBytes: 182000 }],
    }),
  RECEIVED: () =>
    inbound('i1', -1, 'Ye raha ledger.', 'RECEIVED', {
      media: [{ type: 'DOCUMENT', fileName: 'binnys-ledger.pdf', mime: 'application/pdf', sizeBytes: 240000 }],
    }),
  REFUSED: () => inbound('i1', -3, 'Hum ledger share nahi karte, please call karein.', 'REFUSED'),
  BAD_CONTACT: () => inbound('i1', -2, 'Wrong number. Ye number ab use nahi hota.', 'BAD_CONTACT'),
  REDIRECTED: () => inbound('i1', -1, 'Accounts wale Rakesh ji se lijiye: 98xxxxxx21', 'REDIRECTED'),
  QUESTION: () => inbound('i1', -1, 'Kya aap PDF ki jagah Excel le lenge?', 'UNCLEAR'),
  CLAIMS_ALREADY_SENT: () => inbound('i1', -2, 'Humne email par bhej diya hai, check kijiye.', 'CLAIMS_ALREADY_SENT'),
  WRONG_CONTACT: () => inbound('i1', -1, 'Ye mera account nahi hai, galat number.', 'WRONG_CONTACT'),
  DOC_UNREADABLE: () =>
    inbound('i1', -1, null, 'DOC_UNREADABLE', {
      note: 'File is password-protected',
      media: [{ type: 'DOCUMENT', fileName: 'ledger-locked.pdf', mime: 'application/pdf', sizeBytes: 96000 }],
    }),
  DOC_NOT_STATEMENT: () =>
    inbound('i1', -1, null, 'DOC_NOT_STATEMENT', {
      note: 'This looks like an invoice, not a ledger statement',
      media: [{ type: 'IMAGE', fileName: null, mime: 'image/jpeg', sizeBytes: 74000 }],
    }),
}

export function sampleActivity(id) {
  const s = findScenario(id)
  if (!s?.req) return null
  const r = s.req
  const days = Math.max(2, Math.round((Date.now() - new Date(r.requestedAt).getTime()) / DAY))
  const items = [
    out('o1', -days, 'Namaste, please share your ledger statement with us for reconciliation.'),
    out('o2', -Math.ceil(days / 2), 'Gentle reminder — please share your ledger statement.'),
  ]
  if (r.sendFailed) items.push(out('o3', -1, 'Reminder: please share the ledger.', 'FAILED'))
  const reply = REPLIES[r.reply ?? (r.disputed ? 'DISPUTED' : r.state)]
  if (reply) items.push(reply(s))
  items.sort((a, b) => new Date(b.at) - new Date(a.at))
  const outs = items.filter((i) => i.direction === 'OUT').length
  const counts = { WHATSAPP: outs, EMAIL: 0, CALL: 0, OTHER: 0, total: outs, failed: r.sendFailed ? 1 : 0, failedByChannel: r.sendFailed ? { WHATSAPP: 1 } : {} }
  return {
    items,
    counts,
    conversation: sampleRequest(s).conversation,
    suggestedTemplate: null,
    redirect: null,
    numbers: s.wa ? [{ e164: s.wa, name: s.name, via: 'client', from: null }] : [],
    pendingSend: null,
  }
}

// ── Ledgers drawer (ClientLedgers) ───────────────────────────────────────────

const monthLabel = (iso) =>
  new Date(`${iso.slice(0, 7)}-01T00:00:00Z`).toLocaleDateString('en-IN', { month: 'short', year: 'numeric', timeZone: 'UTC' })

export function sampleClientDocuments(clientCode) {
  const s = findScenario(clientCode)
  if (!s) return { folders: [], openFolder: null, page: 1, limit: 5, total: 0, hasMore: false }
  const r = s.req
  const file = (key, label, n) => ({
    key,
    requestId: `${reqId(s)}-${n}`,
    label,
    name: `${code(s)}-statement.pdf`,
    sizeKB: 184,
  })

  // Two past cycles for everyone who's been asked — one whose months could
  // be read off the file, one that was a photo (months unreadable).
  const past = r
    ? [
        {
          id: `${reqId(s)}-p1`,
          receivedOn: day(-40),
          periodLabel: 'Sample',
          coversFrom: fyStart(),
          coversTo: monthEnd(-3),
          files: [file('CLIENT', "Client's ledger", 'p1')],
        },
        {
          id: `${reqId(s)}-p2`,
          receivedOn: day(-75),
          periodLabel: 'Sample',
          coversFrom: null,
          coversTo: null,
          files: [file('CLIENT', "Client's ledger", 'p2')],
        },
      ]
    : []
  const current = r?.their?.receivedAt
    ? [
        {
          id: `${reqId(s)}-c`,
          receivedOn: r.their.receivedAt.slice(0, 10),
          periodLabel: 'Sample',
          coversFrom: fyStart(),
          coversTo: monthEnd(-1),
          files: [file('CLIENT', "Client's ledger", 'c')],
        },
      ]
    : []

  const started = r?.requestedAt ?? null
  const openFolder =
    r && !r.their?.receivedAt
      ? {
          id: reqId(s),
          receivedOn: null,
          periodLabel: 'Sample',
          periodFrom: fyStart(),
          periodTo: monthEnd(-1),
          months: started
            ? [...new Set([started.slice(0, 7), day(0).slice(0, 7)])].map((m) => ({
                label: monthLabel(m),
                closed: monthEnd(0).slice(0, 7) !== m,
              }))
            : [],
          chaseStartedOn: started?.slice(0, 10) ?? null,
          files: [],
        }
      : null

  const folders = [...current, ...past]
  return { folders, openFolder, page: 1, limit: 5, total: folders.length, hasMore: false }
}

// ── Reconciler: tracker + test-bench history ─────────────────────────────────

/** Every tracker stage, each on one of the sample clients. */
export function sampleTrackerRows() {
  const row = (s, over) => ({
    ledgerRequestId: reqId(s),
    sample: true,
    clientCode: code(s),
    clientName: s.name,
    cycleFrom: (s.req?.requestedAt ?? at(-10)).slice(0, 10),
    cycleTo: day(0),
    periodFrom: fyStart(),
    periodTo: monthEnd(-1),
    periodLabel: 'Sample',
    coversFrom: null,
    coversTo: null,
    collectionStatus: 'REQUESTED',
    stage: 'collecting',
    waitingOn: 'statement',
    reconcilable: false,
    ourSideError: null,
    latestRun: null,
    pendingJob: null,
    ...over,
  })
  const got = (s, over) =>
    row(s, {
      collectionStatus: 'READY',
      cycleTo: (s.req?.their?.receivedAt ?? at(-3)).slice(0, 10),
      coversFrom: fyStart(),
      coversTo: monthEnd(-1),
      reconcilable: true,
      waitingOn: null,
      ...over,
    })
  const run = (n, status, side, extra = {}) => ({
    runId: `sample-run-${n}`,
    status,
    failureReason: status === 'failure' ? 'invalid_json_response' : null,
    errorMessage: status === 'failure' ? 'Claude returned no answer text at all.' : null,
    rectificationSide: side,
    createdAt: at(-2, 16),
    excelFilename: status === 'success' ? `Reconciliation - SAMPLE-${n}.xlsx` : null,
    ...extra,
  })
  const S = Object.fromEntries(SCENARIOS.map((s) => [s.n, s]))
  return [
    row(S['03']),
    row(S['11'], { collectionStatus: 'RECEIVED', waitingOn: 'confirmation', coversFrom: fyStart(), coversTo: monthEnd(-1) }),
    row(S['17'], { collectionStatus: 'RECEIVED', waitingOn: 'our_side', ourSideError: S['17'].req.ours.lastError }),
    got(S['20'], { stage: 'ready' }),
    got(S['12'], { stage: 'clean', latestRun: run('12', 'success', 'none') }),
    got(S['13'], { stage: 'needs_client_fix', waitingOn: 'needs_person', postRecoAsks: 7, latestRun: run('13', 'success', 'client') }),
    got(S['05'], { stage: 'needs_our_fix', latestRun: run('05', 'success', 'ours'), coversFrom: null, coversTo: null }),
    got(S['07'], { stage: 'needs_both_fix', latestRun: run('07', 'success', 'both') }),
    got(S['09'], { stage: 'reconciling', pendingJob: { jobId: 'sample-job-09', processingStatus: 'in_progress', startedAt: at(0, 9) } }),
    got(S['16'], { stage: 'failed', latestRun: run('16', 'failure', null) }),
  ]
}

/** Test-bench history: a clean run, one with issues, a failure, one in flight. */
export function sampleRunHistory() {
  const file = (name, size) => ({ filename: name, mime: 'application/pdf', sizeBytes: size })
  return [
    {
      _id: 'sample-hist-4',
      kind: 'pendingBatch',
      jobId: 'sample-hist-job',
      createdAt: at(0, 9, 30),
      model: 'claude-haiku-4.5',
      processingStatus: 'in_progress',
      oursFile: file('Sample - Binny ledger.csv', 26000),
      clientFile: file('Sample - client statement.pdf', 184000),
      clientCode: null,
    },
    {
      _id: 'sample-hist-3',
      kind: 'run',
      status: 'success',
      model: 'claude-haiku-4.5',
      durationMs: 412000,
      usage: { inputTokens: 38000, outputTokens: 9200 },
      issues: [],
      rectificationSide: 'none',
      oursFile: file('Sample - Binny ledger.csv', 26000),
      clientFile: file('Sample - clean statement.pdf', 120000),
      excelFilename: 'Reconciliation - Sample clean.xlsx',
      excelS3Key: 'reconciler-exports/sample-clean.xlsx',
      createdAt: at(-1, 15),
      clientCode: null,
    },
    {
      _id: 'sample-hist-2',
      kind: 'run',
      status: 'success',
      model: 'claude-haiku-4.5',
      durationMs: 655000,
      usage: { inputTokens: 52000, outputTokens: 14100 },
      issues: [{ stage: 'reconciliation_check', message: 'Sample: balances end ₹2,40,000 apart — invoice JWS/0452 missing on client side.' }],
      rectificationSide: 'client',
      oursFile: file('Sample - Binny ledger.csv', 31000),
      clientFile: file('Sample - statement with gaps.pdf', 240000),
      excelFilename: 'Reconciliation - Sample with gaps.xlsx',
      excelS3Key: 'reconciler-exports/sample-with-gaps.xlsx',
      createdAt: at(-2, 12),
      clientCode: null,
    },
    {
      _id: 'sample-hist-1',
      kind: 'run',
      status: 'failure',
      failureReason: 'period_mismatch',
      errorMessage: "Sample: the two ledgers' periods don't overlap (Apr–Jun 2025 vs Apr–Aug 2026).",
      model: 'claude-haiku-4.5',
      durationMs: 38000,
      usage: { inputTokens: 21000, outputTokens: 300 },
      issues: [],
      oursFile: file('Sample - Binny ledger.csv', 26000),
      clientFile: file('Sample - last year.pdf', 150000),
      createdAt: at(-4, 10),
      clientCode: null,
    },
  ]
}

// ── Remediation ─────────────────────────────────────────────────────────────

const REM = [
  { n: 1, c: 'SAMPLE-13', cat: 'BILLING', who: 'reshu.singh@binnys.in', amt: 240000, ref: 'JWS/26-27/0452', d: 'Invoice JWS/26-27/0452 booked twice in Gati — cancel the duplicate.', age: -3, work: 'in_progress' },
  { n: 2, c: 'SAMPLE-07', cat: 'TDS', who: 'paresh.tandle@binnys.in', amt: 18450, ref: 'Q1 26-27', d: 'TDS deducted by client for Q1 not booked as TDS receivable.', age: -6, work: 'paused', pauseNote: 'Waiting on the client' },
  { n: 3, c: 'SAMPLE-05', cat: 'CASH_BANK', who: 'supriya.mirgal@binnys.in', amt: 500000, ref: 'UTR HDFCH01249269838', d: 'Receipt of ₹5,00,000 on 12 Aug not posted to this ledger.', age: -1 },
  { n: 4, c: 'SAMPLE-20', cat: 'OTHER', who: 'nirvi.upadhyay@binnys.in', amt: null, ref: null, d: 'Opening balance differs from last year’s closing — needs checking.', age: -9, coveringFor: 'paresh.tandle@binnys.in', coveringName: 'Paresh Tandle' },
  { n: 5, c: 'SAMPLE-12', cat: 'CASH_BANK', who: 'supriya.mirgal@binnys.in', amt: 75000, ref: 'KTK/26-27/2212', d: 'Bank receipt posted to the wrong party.', age: -12, done: true, worked: true, note: 'Moved to the right ledger and re-uploaded.' },
  { n: 6, c: 'SAMPLE-16', cat: 'BILLING', who: 'reshu.singh@binnys.in', amt: 12650, ref: 'JWSR/26-27/0070', d: 'Sale return raised on our side, client has no matching debit note.', age: -15, done: true, note: null },
]

/** Start/pause/resume/done history for a sample, matching its work state. */
function remWork(x, assignee) {
  const base = x.age
  const ev = (action, dayOffset, hour, note = null) => ({ action, at: at(base + dayOffset, hour), by: assignee, note })
  if (x.done) {
    const history = x.worked ? [ev('start', 1, 11), ev('pause', 1, 13, 'Need info from the team'), ev('resume', 2, 10), ev('done', 2, 17, x.note)] : [ev('done', 2, 17, x.note)]
    return { status: 'confirmed', startedAt: x.worked ? at(base + 1, 11) : null, workedMs: x.worked ? 3 * 3_600_000 + 25 * 60_000 : 0, lastResumedAt: null, pausedAt: null, pauseNote: null, history }
  }
  if (x.work === 'in_progress') {
    // Worked 11:00–12:00 on day one, resumed 09:00 today — `workedMs` is the closed hour.
    return { status: 'in_progress', startedAt: at(base + 1, 11), workedMs: 60 * 60_000, lastResumedAt: at(0, 9), pausedAt: null, pauseNote: null, history: [ev('start', 1, 11), ev('pause', 1, 12), { ...ev('resume', 0, 9), at: at(0, 9) }] }
  }
  if (x.work === 'paused') {
    // One 75-minute session yesterday, 14:45–16:00, paused since.
    return { status: 'paused', startedAt: at(-1, 14, 45), workedMs: 75 * 60_000, lastResumedAt: null, pausedAt: at(-1, 16), pauseNote: x.pauseNote, history: [{ ...ev('start', 0, 14), at: at(-1, 14, 45) }, { ...ev('pause', 0, 16, x.pauseNote), at: at(-1, 16) }] }
  }
  return { status: 'open', startedAt: null, workedMs: 0, lastResumedAt: null, pausedAt: null, pauseNote: null, history: [] }
}

/** Start/pause/resume/done done on a sample this session — kept in memory so the screen moves on. */
const REM_STATE = new Map()

function remItem(x, assignee = x.who) {
  return {
    ...(REM_STATE.get(x.n) ?? remWork(x, assignee)),
    originalAccountantEmail: x.coveringFor ?? null,
    originalAccountantName: x.coveringName ?? null,
    _id: `sample-rem-${x.n}`,
    sample: true,
    clientCode: x.c,
    reconciliationRunId: null,
    category: x.cat,
    description: x.d,
    amount: x.amt,
    reference: x.ref,
    channelName: 'ledger-issues',
    channelPostedAt: at(x.age, 10),
    assignedAccountantEmail: assignee,
    assignedAt: at(x.age, 10),
    confirmedAt: x.done ? at(x.age + 2, 17) : null,
    confirmedBy: x.done ? assignee : null,
    confirmedNote: x.done ? x.note : null,
    createdAt: at(x.age, 10),
    updatedAt: at(x.age, 10),
  }
}

export const sampleRemediationItems = () => REM.map((x) => remItem(x))

/** Samples on whoever is signed in, for My Ledgers — the open ones, or (`done`) the finished ones. */
export const sampleMyItems = (email, { done = false } = {}) =>
  REM.filter((x) => Boolean(x.done) === done)
    .slice(0, 3)
    .map((x) => remItem(x, email ?? x.who))

/**
 * Apply start / pause / resume / mark-done to a sample fix, the same way the
 * backend's work transitions would. Returns the updated item, or null if the
 * id isn't a sample or the move isn't allowed from its current state.
 */
export function sampleRemediationAct(id, action, note = null) {
  const x = REM.find((r) => `sample-rem-${r.n}` === id)
  if (!x) return null
  const cur = REM_STATE.get(x.n) ?? remWork(x, x.who)
  const now = new Date().toISOString()
  const since = (v) => (v ? Date.now() - new Date(v).getTime() : 0)
  const ev = { action: action === 'mark-done' ? 'done' : action, at: now, by: x.who, note }
  // One fix in progress at a time, same as the backend.
  if (action === 'start' || action === 'resume') {
    const busy = REM.find((r) => r !== x && (REM_STATE.get(r.n) ?? remWork(r, r.who)).status === 'in_progress')
    if (busy) return { blockedBy: busy.c }
  }
  let next = null
  if (action === 'start' && cur.status === 'open') {
    next = { ...cur, status: 'in_progress', startedAt: now, lastResumedAt: now }
  } else if (action === 'pause' && cur.status === 'in_progress') {
    next = { ...cur, status: 'paused', workedMs: cur.workedMs + since(cur.lastResumedAt), lastResumedAt: null, pausedAt: now, pauseNote: note }
  } else if (action === 'resume' && cur.status === 'paused') {
    next = { ...cur, status: 'in_progress', lastResumedAt: now, pausedAt: null, pauseNote: null }
  } else if (action === 'mark-done' && cur.status !== 'confirmed') {
    next = { ...cur, status: 'confirmed', workedMs: cur.workedMs + since(cur.lastResumedAt), lastResumedAt: null, pausedAt: null, pauseNote: null }
    x.done = true
    x.note = note
  }
  if (!next) return null
  next.history = [...(cur.history ?? []), ev]
  REM_STATE.set(x.n, next)
  return sampleRemediationItem(id)
}

export function sampleRemediationItem(id) {
  const x = REM.find((r) => `sample-rem-${r.n}` === id)
  if (!x) return null
  return {
    ...remItem(x),
    nudgeMessagePreview: x.done
      ? null
      : 'Hi — you have 3 ledger fixes open in Lereco (oldest 9 days). Open My Ledgers to see them and mark each done once re-uploaded to Gati.',
  }
}

// ── Reports ─────────────────────────────────────────────────────────────────

/** One report row per sample cycle, shaped like `reports.service.js`'s `ledgers`. */
export function sampleReportLedgers() {
  const stage = new Map(sampleTrackerRows().map((t) => [t.clientCode, t]))
  return SCENARIOS.filter((s) => s.req).map((s) => {
    const r = s.req
    const t = stage.get(code(s))
    const wa = r.chase.WHATSAPP ?? 0
    const em = r.chase.EMAIL ?? 0
    const replied = r.disputed || Boolean(r.reply) || !['AWAITING_REPLY'].includes(r.state)
    return {
      ledgerRequestId: reqId(s),
      sample: true,
      clientCode: code(s),
      clientName: s.name,
      periodFrom: fyStart(),
      periodLabel: 'Sample',
      collectionStatus: r.status ?? 'REQUESTED',
      openedAt: r.requestedAt,
      firstAskedAt: r.requestedAt,
      conversationState: r.state,
      disputed: Boolean(r.disputed),
      promisedFor: r.promisedFor ?? null,
      sendFailing: Boolean(r.sendFailed),
      waOut: wa,
      emailOut: em,
      callOut: 0,
      delivered: Math.max(0, wa + em - (r.sendFailed ? 1 : 0)),
      read: Math.max(0, wa - 1),
      failed: r.sendFailed ? 1 : 0,
      inbound: replied ? 1 + (r.unread ?? 0) : 0,
      lastOutAt: at(-1),
      firstReplyAt: replied ? at(-2, 14) : null,
      lastReplyAt: replied ? at(-1, 14) : null,
      receivedAt: r.their?.receivedAt ?? null,
      receivedVia: r.their?.source ?? null,
      confirmedAt: r.their?.confirmedAt ?? null,
      coversFrom: t?.coversFrom ?? null,
      coversTo: t?.coversTo ?? null,
      ourSidePulledAt: r.ours ? at(-1) : null,
      ourSideError: r.ours?.lastError ?? null,
      stage: t?.stage ?? 'collecting',
      waitingOn: t?.waitingOn ?? 'statement',
      runs: t?.latestRun ? (t.stage === 'failed' ? 2 : 1) : t?.pendingJob ? 1 : 0,
      runsSucceeded: t?.latestRun?.status === 'success' ? 1 : 0,
      runsFailed: t?.stage === 'failed' ? 2 : 0,
      firstRunAt: t?.latestRun?.createdAt ?? null,
      lastRunAt: t?.latestRun?.createdAt ?? null,
      lastRunStatus: t?.latestRun?.status ?? null,
      rectificationSide: t?.latestRun?.rectificationSide ?? null,
      fixesOpen: REM.filter((x) => x.c === code(s) && !x.done).length,
      fixesDone: REM.filter((x) => x.c === code(s) && x.done).length,
    }
  })
}

/** Internal fixes, shaped like `reports.service.js`'s `items`. */
export function sampleReportItems() {
  return sampleRemediationItems().map((i) => ({
    _id: i._id,
    sample: true,
    clientCode: i.clientCode,
    category: i.category,
    amount: i.amount,
    status: i.status,
    assignedAccountantEmail: i.assignedAccountantEmail,
    createdAt: i.createdAt,
    startedAt: i.startedAt,
    confirmedAt: i.confirmedAt,
    workedMs: i.workedMs,
    lastResumedAt: i.lastResumedAt,
    pausedAt: i.pausedAt,
    pauseNote: i.pauseNote,
    pauses: i.history.filter((h) => h.action === 'pause').length,
    lastActivityAt: i.history.at(-1)?.at ?? null,
  }))
}

